import React, { useEffect, useMemo, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import './PhoneAIWalletHandoff.css';

// Phase 4d: PHONE prepares a transaction (or sequence of transactions) and
// hands it to the user's wallet for signing. PHONE NEVER signs or holds a key.
//
// Flow:
//   1. We render the prepared txs with humanized labels.
//   2. User clicks "Sign in wallet" — we send each tx via the connected
//      wallet (eth_sendTransaction). Each tx pops a wallet confirmation.
//   3. After every tx is broadcast, we POST the FINAL tx hash to /wallet-ack
//      via the onSigned callback (which calls walletAckPhoneAI).
//   4. If a tx is rejected mid-batch, we stop and surface the error — no
//      /wallet-ack call happens (token will expire and PHONE re-proposes
//      next time).

const humanizeTool = (raw) =>
  String(raw || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const BSC_CHAIN_HEX = '0x38'; // 56
const BSC_CHAIN_PARAMS = {
  chainId: '0x38',
  chainName: 'BNB Smart Chain',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: ['https://bsc-dataseed.bnbchain.org/'],
  blockExplorerUrls: ['https://bscscan.com/'],
};

const ensureBscChain = async (provider) => {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BSC_CHAIN_HEX }],
    });
  } catch (e) {
    // 4902 = chain not added. Try to add then switch.
    if (e?.code === 4902 || /unrecognized chain/i.test(String(e?.message || ''))) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [BSC_CHAIN_PARAMS],
      });
    } else if (e?.code === 4001) {
      throw new Error('You rejected the chain switch. Please switch to BNB Smart Chain (56) in your wallet.');
    } else {
      throw e;
    }
  }
};

const waitForReceipt = async (web3, txHash, timeoutMs = 90000) => {
  // Poll for a receipt. web3.js v4 doesn't always block reliably for receipts
  // when the tx is from `eth_sendTransaction` rather than `contract.send()`,
  // so we poll here for portability.
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await web3.eth.getTransactionReceipt(txHash);
      if (r) return r;
    } catch (_) {}
    await new Promise((res) => setTimeout(res, 2500));
  }
  return null;
};

const PhoneAIWalletHandoff = ({ pendingWalletTx, onSigned, onCancel, running }) => {
  const { web3, account, currentProvider } = useWeb3();
  const [secondsLeft, setSecondsLeft] = useState(
    Math.max(0, Math.floor((pendingWalletTx?.expires_in_ms || 0) / 1000))
  );
  const [step, setStep] = useState(0); // index of tx currently being signed
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSecondsLeft(Math.max(0, Math.floor((pendingWalletTx?.expires_in_ms || 0) / 1000)));
    setStep(0);
    setError('');
  }, [pendingWalletTx]);

  useEffect(() => {
    if (!pendingWalletTx) return undefined;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [pendingWalletTx]);

  const isDanger = useMemo(() => {
    const s = String(pendingWalletTx?.summary || '');
    return s.includes('⚠') || /IRREVERSIBLE/i.test(s);
  }, [pendingWalletTx]);

  if (!pendingWalletTx) return null;
  const txs = Array.isArray(pendingWalletTx.txs) ? pendingWalletTx.txs : [];
  const expired = secondsLeft <= 0;

  const sign = async () => {
    if (!web3 || !account || !currentProvider) {
      setError('No wallet connected. Connect your wallet first and try again.');
      return;
    }
    setError('');
    setBusy(true);

    try {
      await ensureBscChain(currentProvider);
    } catch (e) {
      setError(e?.message || 'Could not switch to BNB Smart Chain.');
      setBusy(false);
      return;
    }

    let lastTxHash = null;
    for (let i = 0; i < txs.length; i++) {
      setStep(i);
      const tx = txs[i];
      try {
        // We use eth_sendTransaction directly so the user's wallet renders
        // the tx as a generic call (with `to`, `data`, `value`). Wallets like
        // MetaMask will decode the calldata and show the function name.
        const hash = await new Promise((resolve, reject) => {
          currentProvider
            .request({
              method: 'eth_sendTransaction',
              params: [{
                from: account,
                to: tx.to,
                data: tx.data || '0x',
                value: tx.value || '0x0',
              }],
            })
            .then(resolve)
            .catch(reject);
        });
        lastTxHash = hash;
        // Wait for confirmation BEFORE sending the next tx — particularly
        // important for approve+deposit batches where the second tx depends
        // on the first being mined.
        if (i < txs.length - 1) {
          await waitForReceipt(web3, hash, 90000);
        }
      } catch (e) {
        const msg = e?.message || String(e);
        // 4001 = user rejected. Other = chain/RPC error.
        if (e?.code === 4001 || /reject/i.test(msg)) {
          setError(`Cancelled — you rejected ${tx.label || `step ${i + 1}`} in your wallet.`);
        } else {
          setError(`Step ${i + 1} (${tx.label || tx.fn_name}) failed: ${msg.slice(0, 200)}`);
        }
        setBusy(false);
        return;
      }
    }

    setBusy(false);
    if (lastTxHash) onSigned(pendingWalletTx.action_token, lastTxHash);
  };

  return (
    <div className={`phoneai-wallet-handoff ${isDanger ? 'phoneai-wallet-handoff--danger' : ''}`}>
      <div className="phoneai-wallet-handoff-head">
        <span className="phoneai-wallet-handoff-badge">
          {isDanger ? '⚠ Sign in wallet — irreversible' : 'Sign in your wallet'}
        </span>
        <span className="phoneai-wallet-handoff-tool">{humanizeTool(pendingWalletTx.tool)}</span>
      </div>

      <div className="phoneai-wallet-handoff-summary">{pendingWalletTx.summary}</div>

      <div className="phoneai-wallet-handoff-steps">
        {txs.map((tx, i) => (
          <div
            key={i}
            className={`phoneai-wallet-handoff-step ${i === step && busy ? 'phoneai-wallet-handoff-step--active' : ''} ${i < step ? 'phoneai-wallet-handoff-step--done' : ''}`}
          >
            <span className="phoneai-wallet-handoff-step-idx">{i + 1}.</span>
            <span className="phoneai-wallet-handoff-step-label">{tx.label || tx.fn_name || `Tx ${i + 1}`}</span>
            {i < step && <span className="phoneai-wallet-handoff-step-mark">✓</span>}
          </div>
        ))}
      </div>

      <details className="phoneai-wallet-handoff-details">
        <summary>Show raw call details</summary>
        <pre className="phoneai-wallet-handoff-raw">{JSON.stringify(txs, null, 2)}</pre>
      </details>

      <div className="phoneai-wallet-handoff-meta">
        {expired
          ? 'This proposal expired — ask PHONE again to re-prepare.'
          : `Expires in ${secondsLeft}s · Network: BNB Smart Chain (56)`}
        {!account && ' · No wallet connected'}
      </div>

      {error && <div className="phoneai-wallet-handoff-error">{error}</div>}

      <div className="phoneai-wallet-handoff-actions">
        <button
          type="button"
          className="phoneai-wallet-handoff-cancel"
          onClick={onCancel}
          disabled={busy || running}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`phoneai-wallet-handoff-go ${isDanger ? 'phoneai-wallet-handoff-go--danger' : ''}`}
          onClick={sign}
          disabled={busy || running || expired || !account}
        >
          {busy
            ? `Signing step ${step + 1} of ${txs.length}…`
            : running
              ? 'Recording…'
              : txs.length === 1
                ? 'Sign in wallet'
                : `Sign ${txs.length} txs in wallet`}
        </button>
      </div>
    </div>
  );
};

export default PhoneAIWalletHandoff;
